/**
 * @fileoverview Idempotency key hook.
 * @description Hands out the Idempotency-Key for a lifecycle command. A key belongs to the logical
 * action on a user ("deactivate EMP-2004"), is made the first time that action is sent, and is kept
 * while its outcome is unknown: a lost response, a timeout or abort (no answer came back at all), a
 * 408, or any 5xx such as a 504 from a gateway that gave up while the backend carried on. Retrying
 * then sends the same key, so the backend recognises a replay rather than a second command (e.g. a
 * second welcome-back email or a second invitation restart), even when the dialog was closed and
 * reopened, the note was edited or the user's version changed in between. A definite answer
 * settles the action and drops the key: a success, so the same action later (deactivate,
 * reactivate, deactivate again) is a new command and is not replayed from the first response, and a
 * 4xx refusal (a 409, a step-up 403, a 412), which a backend that caches the outcome per key would
 * otherwise replay. The keys live in a registry on the QueryClient (idempotency-keys.utils.ts), not
 * in the component, so they outlive the dialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { idempotencyKeysOf } from '@/features/users/utils/idempotency-keys.utils'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

import type { ErrorCode } from '@/shared/constants/error-codes.constants'

/** From this HTTP status up the server may have acted although it answered with an error. */
const SERVER_ERROR_STATUS = 500
/** A request timeout: the server gave up waiting, so the outcome is unknown. */
const REQUEST_TIMEOUT_STATUS = 408

/** What a failed attempt's error says: the code and, when a response came, its status. */
export interface IdempotencyFailure {
  code: ErrorCode
  status?: number | undefined
}

export interface IdempotencyKeys {
  /** The key for an action's scope: the one already issued while its outcome is unknown. */
  keyFor: (scope: string) => string
  /** Reports a success, so the same action sent later is a new command with a new key. */
  settleSuccess: (scope: string) => void
  /** Reports a failed attempt, dropping the key unless the outcome is still unknown. */
  settleFailure: (scope: string, error: IdempotencyFailure) => void
}

/**
 * @description Tells whether a failure leaves the command's outcome unknown, so a retry must be a
 * replay: no response at all (network error, timeout, abort), a 408, or a 5xx. When a response
 * came, its status decides: the service-unavailable code is shared by many backend app codes that
 * arrive as definite 4xx refusals, so it only counts when there is no status to go by.
 * @param {IdempotencyFailure} error - The failed attempt's error.
 * @returns {boolean} True when the key must be kept for the retry.
 */
export function isOutcomeUnknown(error: IdempotencyFailure): boolean {
  if (error.status !== undefined) {
    return error.status === REQUEST_TIMEOUT_STATUS || error.status >= SERVER_ERROR_STATUS
  }

  return error.code === ERROR_CODES.NETWORK_ERROR || error.code === ERROR_CODES.SERVICE_UNAVAILABLE
}

/**
 * @description Keeps one Idempotency-Key per action on a user, reusing it only for a retry whose
 * outcome is unknown, and clearing it once the action succeeded or was definitely refused.
 * @returns {IdempotencyKeys} `keyFor`, which returns the key for an action's scope (1-100 of
 * [A-Za-z0-9._:-], which a UUID is), `settleSuccess` for a success and `settleFailure` for each
 * failed attempt's error.
 */
export function useIdempotencyKey(): IdempotencyKeys {
  const queryClient = useQueryClient()
  const keys = useMemo(() => idempotencyKeysOf(queryClient), [queryClient])

  /**
   * @description Returns the key for an action's scope, making a new one only when none is in use.
   * @param {string} scope - The action on a user (see idempotencyScope).
   * @returns {string} The Idempotency-Key to send.
   */
  const keyFor = useCallback(
    (scope: string): string => {
      const issued = keys.get(scope)
      if (issued) {
        return issued
      }
      const key = crypto.randomUUID()
      keys.set(scope, key)
      return key
    },
    [keys],
  )

  /**
   * @description Forgets the key after a success: the action is done, and the same action sent
   * later is a new one that must not be answered with the first response.
   * @param {string} scope - The action on a user.
   */
  const settleSuccess = useCallback(
    (scope: string): void => {
      keys.delete(scope)
    },
    [keys],
  )

  /**
   * @description Forgets the key after a failure that was a definite answer, keeping it when the
   * outcome is unknown (no response, a timeout, an abort, a 408 or a 5xx), where a retry must be a
   * replay.
   * @param {string} scope - The action on a user.
   * @param {IdempotencyFailure} error - The failed attempt's error.
   */
  const settleFailure = useCallback(
    (scope: string, error: IdempotencyFailure): void => {
      if (!isOutcomeUnknown(error)) {
        keys.delete(scope)
      }
    },
    [keys],
  )

  return useMemo(
    () => ({ keyFor, settleSuccess, settleFailure }),
    [keyFor, settleSuccess, settleFailure],
  )
}
